import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import type { Product, ProductListItem, Warehouse } from '../api/types'
import { formatMoney, formatQty } from '../ui/format'
import {
  Banner, Empty, ErrorBanner, Field, Panel, Select, Spinner, TextInput,
} from '@shop/ui'

/** حركةٌ في كشف المادّة — كما يردّها `/reports/variant-ledger`. */
type Movement = {
  occurredAt: string
  day: string
  movementNo: string
  movementType: string
  direction: number
  qty: number
  signedQty: number
  runningBalance: number
  unitCost: number | null
  sourceType: string | null
  sourceNo: string | null
  reasonCode: string | null
  note: string | null
}

type Ledger = {
  sku: string
  productName: string
  warehouseName: string
  openingBalance: number
  closingBalance: number
  totalIn: number
  totalOut: number
  movements: Movement[]
}

/**
 * اسمُ الحركة كما يقرؤه صاحبُ المحلّ — <b>لا رمزُها في القاعدة</b>.
 *
 * ⚠ <b>والمصدرُ والسببُ يسبقان النوع</b>: «تسويةٌ داخلة» لا تقول شيئًا، و
 *   «استلامُ بضاعة» أو «فاتورةُ كلفة» تقول ما جرى. فالنوعُ آخرُ ما يُلجأ إليه.
 */
function describe(m: Movement): string {
  if (m.sourceType === 'COST_INVOICE') return 'فاتورةُ كلفة'
  if (m.reasonCode === 'PURCHASE_PENDING') return 'استلامُ بضاعة'
  if (m.reasonCode === 'COUNT_SURPLUS') return 'زيادةُ جرد'
  if (m.reasonCode === 'COUNT_SHORTAGE') return 'نقصُ جرد'
  if (m.reasonCode === 'DAMAGE') return 'تلف'
  if (m.reasonCode === 'LOST') return 'فقدان'
  if (m.reasonCode === 'CORRECTION') return 'تصحيح'

  const names: Record<string, string> = {
    OPENING: 'افتتاح',
    PURCHASE: 'شراء',
    PURCHASE_RETURN: 'مرتجعٌ للمورّد',
    SALE: 'بيع',
    SALE_RETURN: 'مرتجعُ زبون',
    TRANSFER_OUT: 'تحويلٌ خارج',
    TRANSFER_IN: 'تحويلٌ داخل',
    ADJUST_IN: 'تسويةٌ داخلة',
    ADJUST_OUT: 'تسويةٌ خارجة',
    COUNT_ADJUST: 'جرد',
    DAMAGE: 'تلف',
    REVERSAL: 'عكسُ حركة',
  }

  return names[m.movementType] ?? m.movementType
}

/** يومُ عملٍ بتوقيت عمّان بصيغة `YYYY-MM-DD`. */
function day(offset = 0): string {
  const at = new Date(Date.now() + offset * 86_400_000)

  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Amman' }).format(at)
}

/**
 * حركةُ المادّة — <b>كلُّ ما دخل وخرج من صنفٍ بين تاريخين، ورصيدُه بعد كلّ حركة</b>.
 *
 * <p>
 * ⚠ <b>طلبُ المالك ٢٠٢٦-٠٩-٢٨.</b> والتقريرُ كان مبنيًّا في الخادم
 * (<c>/reports/variant-ledger</c>) <b>بلا شاشةٍ واحدة</b> — فلا يصل إليه أحد.
 * </p>
 *
 * <p>
 * ⚠ <b>والرصيدُ الجاري يُحسب في الخادم لا هنا</b>: يبدأ من رصيد أوّل المدّة
 * (كلُّ ما قبلها)، فحصرُ المدّة لا يبدأ الحسابَ من صفرٍ كاذب.
 * </p>
 */
export default function ItemMovement() {
  const { session, can } = useAuth()

  const [term, setTerm] = useState('')
  const [productId, setProductId] = useState<string | null>(null)
  const [variantId, setVariantId] = useState('')
  const [chosenWarehouse, setChosenWarehouse] = useState('')
  const [from, setFrom] = useState(day(-30))
  const [to, setTo] = useState(day())

  const branchId = session?.me.activeBranchId ?? ''
  const seesCost = can(Perm.costView)

  const found = useQuery({
    queryKey: ['movement-search', term.trim()],
    queryFn: () => api<ProductListItem[]>(
      `/api/v1/products?limit=20&q=${encodeURIComponent(term.trim())}`),
    enabled: term.trim().length >= 2 && productId === null,
    placeholderData: keepPreviousData,
  })

  const product = useQuery({
    queryKey: ['product', productId],
    queryFn: () => api<Product>(`/api/v1/products/${productId}`),
    enabled: productId !== null,
  })

  const warehouses = useQuery({
    queryKey: ['warehouses', branchId],
    queryFn: () => api<Warehouse[]>(`/api/v1/warehouses?branchId=${branchId}`),
    enabled: branchId !== '',
  })

  // ⚠ **والمتغيّرُ الوحيدُ يُختار وحدَه** — أكثرُ الأصناف لها قياسٌ واحد، وسؤالُ
  //   المالك «أيَّ قياس؟» عن صنفٍ بلا قياسات خطوةٌ لا معنى لها.
  const variants = product.data?.variants ?? []
  const variant = variantId || (variants.length === 1 ? variants[0].id : '')
  const warehouseId = chosenWarehouse || warehouses.data?.[0]?.id || ''

  const ledger = useQuery({
    queryKey: ['variant-ledger', variant, warehouseId, from, to],
    queryFn: () => api<Ledger>(
      `/api/v1/reports/variant-ledger?variantId=${variant}&warehouseId=${warehouseId}`
      + `&from=${from}&to=${to}`),
    enabled: variant !== '' && warehouseId !== '' && from !== '' && to !== '',
  })

  const reset = () => {
    setProductId(null)
    setVariantId('')
    setTerm('')
  }

  if (!can(Perm.reportViewMine)) {
    return (
      <Panel title="حركةُ المادّة">
        <Banner tone="warn">هذه الشاشةُ تحتاج صلاحيّةَ «التقارير».</Banner>
      </Panel>
    )
  }

  const data = ledger.data

  return (
    <Panel title="حركةُ المادّة">
      {productId === null ? (
        <>
          <Field label="الصنف" hint="الاسمُ أو الرمزُ أو الباركود">
            <TextInput value={term} onChange={setTerm} placeholder="ابحث…" />
          </Field>

          {found.isFetching && <Spinner label="يُبحث…" />}

          <ul className="mt-2 grid gap-1">
            {(term.trim().length >= 2 ? found.data ?? [] : []).map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className="w-full rounded-lg border border-line px-3 py-2 text-right hover:bg-surface-raised"
                  onClick={() => setProductId(p.id)}
                >
                  <span className="font-semibold">{p.nameAr}</span>
                  <span className="tabular mr-2 text-xs text-ink-soft" dir="ltr">{p.code}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-line px-3 py-2">
          <span className="font-semibold">{product.data?.nameAr ?? '…'}</span>
          <button type="button" className="text-sm underline" onClick={reset}>صنفٌ آخر</button>
        </div>
      )}

      {productId !== null && (
        <div className="grid gap-3 sm:grid-cols-4">
          {variants.length > 1 && (
            <Field label="القياس">
              <Select value={variant} onChange={setVariantId}>
                <option value="">— اختر —</option>
                {variants.map((v) => (
                  <option key={v.id} value={v.id}>
                    {[v.variantName, v.colour, v.capacity].filter(Boolean).join(' · ') || v.sku}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          {(warehouses.data ?? []).length > 1 && (
            <Field label="المستودع">
              <Select value={warehouseId} onChange={setChosenWarehouse}>
                {(warehouses.data ?? []).map((w) => (
                  <option key={w.id} value={w.id}>{w.nameAr}</option>
                ))}
              </Select>
            </Field>
          )}

          <Field label="من">
            <TextInput value={from} onChange={setFrom} placeholder="2026-09-01" />
          </Field>

          <Field label="إلى">
            <TextInput value={to} onChange={setTo} placeholder="2026-09-30" />
          </Field>
        </div>
      )}

      <ErrorBanner error={found.error ?? product.error ?? ledger.error} />

      {ledger.isFetching && <div className="mt-3"><Spinner label="يُقرأ الدفتر…" /></div>}

      {data && (
        <>
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label="رصيدُ أوّل المدّة" value={formatQty(data.openingBalance)} />
            <Stat label="وارد" value={`+${formatQty(data.totalIn)}`} tone="ok" />
            <Stat label="صادر" value={`−${formatQty(data.totalOut)}`} tone="warn" />
            <Stat label="رصيدُ آخر المدّة" value={formatQty(data.closingBalance)} strong />
          </div>

          {data.movements.length === 0 ? (
            <div className="mt-3"><Empty>لا حركةَ لهذا الصنف في هذه المدّة.</Empty></div>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-ink-soft">
                  <tr className="border-b border-line">
                    <th className="p-2 text-right">التاريخ</th>
                    <th className="p-2 text-right">الحركة</th>
                    <th className="p-2 text-right">المستند</th>
                    <th className="p-2 text-left">وارد</th>
                    <th className="p-2 text-left">صادر</th>
                    <th className="p-2 text-left">الرصيد</th>
                    {seesCost && <th className="p-2 text-left">الكلفة</th>}
                  </tr>
                </thead>

                <tbody>
                  {data.movements.map((m) => (
                    <tr key={m.movementNo} className="border-b border-line/50" title={m.note ?? undefined}>
                      <td className="tabular p-2 text-xs text-ink-soft" dir="ltr">{m.day}</td>
                      <td className="p-2">{describe(m)}</td>
                      <td className="tabular p-2 text-xs text-ink-soft" dir="ltr">{m.sourceNo ?? m.movementNo}</td>
                      <td className="tabular p-2 text-left text-ok" dir="ltr">
                        {m.signedQty > 0 ? formatQty(m.signedQty) : ''}
                      </td>
                      <td className="tabular p-2 text-left text-warn" dir="ltr">
                        {m.signedQty < 0 ? formatQty(-m.signedQty) : ''}
                      </td>
                      <td className="tabular p-2 text-left font-bold" dir="ltr">{formatQty(m.runningBalance)}</td>
                      {seesCost && (
                        <td className="tabular p-2 text-left text-xs text-ink-soft" dir="ltr">
                          {m.unitCost === null ? '—' : formatMoney(m.unitCost)}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Panel>
  )
}

function Stat({ label, value, tone, strong }: {
  label: string
  value: string
  tone?: 'ok' | 'warn'
  strong?: boolean
}) {
  return (
    <div className="rounded-xl border border-line bg-surface-raised p-3">
      <div className="text-xs text-ink-soft">{label}</div>
      <div
        className={`tabular mt-1 text-xl ${strong ? 'font-bold' : ''} ${
          tone === 'ok' ? 'text-ok' : tone === 'warn' ? 'text-warn' : ''
        }`}
        dir="ltr"
      >
        {value}
      </div>
    </div>
  )
}
